import React, { useEffect, useState } from "react";
import { AccessibilityInfo, StyleSheet, View } from "react-native";
import { MotiView } from "moti";
import { CheckCircle2 } from "lucide-react";
import CustomText from "../../../common/general/CustomText/CustomText";
import { colors, radii, spacing } from "../../../../styles/design-system";
import type { ObjectiveTransitionData } from "../../../../containers/companion/companionMessage.types";

export interface ObjectiveTransitionCardProps {
  transition: ObjectiveTransitionData;
}

const CARD_MAX_WIDTH = 704;

export const ObjectiveTransitionCard = ({ transition }: ObjectiveTransitionCardProps) => {
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    let active = true;
    AccessibilityInfo.isReduceMotionEnabled().then((enabled) => {
      if (active) setReduceMotion(enabled);
    });
    const sub = AccessibilityInfo.addEventListener("reduceMotionChanged", setReduceMotion);
    return () => {
      active = false;
      sub.remove();
    };
  }, []);

  const completed = transition.objectives.filter((obj) => obj.status === "completed");
  const current = transition.objectives.find((obj) => obj.status === "current");
  const upcoming = transition.objectives.filter((obj) => obj.status === "upcoming");

  if (completed.length === 0 && !current && upcoming.length === 0) return null;

  const fromState = reduceMotion ? undefined : { opacity: 0, translateY: 8 };

  return (
    <MotiView
      from={fromState}
      animate={{ opacity: 1, translateY: 0 }}
      transition={{ type: "timing", duration: 300 }}
      style={styles.card}>
      {completed.length > 0 && (
        <View style={styles.section}>
          {completed.map((obj) => (
            <View key={obj.id} style={styles.row}>
              <View style={styles.iconBox}>
                <CheckCircle2 size={16} color={colors.success[500]} />
              </View>
              <CustomText
                useTranslationText={false}
                text={obj.label}
                size="s"
                weight="regular"
                style={styles.completedLabel}
              />
            </View>
          ))}
        </View>
      )}

      {current && (
        <View style={[styles.section, completed.length > 0 && styles.sectionDivider]}>
          <CustomText text="objective_transition_card_up_next" size="xs" weight="medium" style={styles.upNextLabel} />
          <CustomText
            useTranslationText={false}
            text={current.label}
            size="s"
            weight="bold"
            style={styles.currentLabel}
          />
        </View>
      )}

      {upcoming.length > 0 && (
        <View style={[styles.section, (current || completed.length > 0) && styles.sectionDivider]}>
          {upcoming.map((obj) => (
            <View key={obj.id} style={styles.row}>
              <View style={styles.iconBox}>
                <View style={styles.upcomingDot} />
              </View>
              <CustomText
                useTranslationText={false}
                text={obj.label}
                size="s"
                weight="regular"
                style={styles.upcomingLabel}
              />
            </View>
          ))}
        </View>
      )}
    </MotiView>
  );
};

const styles = StyleSheet.create({
  card: {
    width: "100%",
    maxWidth: CARD_MAX_WIDTH,
    marginTop: spacing.lg,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: radii.lg,
    padding: spacing.md,
  },
  section: {
    gap: spacing.xs,
  },
  sectionDivider: {
    marginTop: spacing.sm,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.grey[100],
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  iconBox: {
    width: 16,
    height: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  completedLabel: {
    color: colors.grey[500],
  },
  upNextLabel: {
    color: colors.grey[400],
    letterSpacing: 0.5,
    textTransform: "uppercase",
    marginBottom: spacing.xxs,
  },
  currentLabel: {
    color: colors.grey[900],
  },
  upcomingDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.grey[300],
  },
  upcomingLabel: {
    color: colors.grey[400],
  },
});
